import * as React from "react";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";

import {dateFormat, valuationAnalysisOption} from "@/utils/constants";
import {getAlertSeverity} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";
import {useTranslation} from "react-i18next";

export default function ScoutReportsContainer(props) {
	const {
		reports,
		filters,
		fullHeight = true,
		grouped = true,
		breadcrumbs = true,
		playerId,
	} = props;

	const navigate = useNavigate();
	const {t} = useTranslation();

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		onlyPro: true,
	});

	const sortReports = (data) => {
		return data.sort((a, b) => {
			const dateA = new Date(a.player?.dateOfBirth);
			const dateB = new Date(b.player?.dateOfBirth);

			if (a.valuation === 1 && b.valuation !== 1) return 1;
			if (a.valuation !== 1 && b.valuation === 1) return -1;

			return dateB - dateA;
		});
	};

	const columns = [
		{
			label: "Data",
			key: "reportDate",
			customRender: (item) => {
				return moment(item.reportDate).format(dateFormat);
			},
			medium: true,
		},
		{
			label: "Segnalatore",
			key: "signalerName",
			align: "start",
		},
	];

	if (!playerId) {
		columns.push(
			{
				label: "Giocatore",
				key: "firstName",
				customRender: (item) => {
					return item?.player?.firstName + " " + item?.player?.lastName;
				},
				align: "start",
			},
			{
				label: "Ruolo",
				key: "role",
				customRender: (item) => {
					return item?.player?.goalkeeper
						? "P"
						: item?.player?.primaryRole?.[0];
				},
				small: true,
				align: "start",
			},
			{
				label: t("birthdate"),
				key: "dateOfBirth",
				customRender: (item) => {
					return item?.player?.dateOfBirth
						? moment(item?.player?.dateOfBirth).format(dateFormat)
						: "---";
				},
				medium: true,
			},
		);
	}

	columns.push(
		{
			label: "Squadra",
			key: "club",
			customRender: (item) => {
				return item?.club?.name;
			},
			medium: true,
			align: "start",
		},
		{
			label: "Valutazione",
			key: "valuation",
			customRender: (item) => {
				const alertData = getAlertSeverity(item.valuation) || {
					severity: "",
					message: "",
				};
				return (
					<div className={"severity " + (alertData.severity || "")}>
						{alertData.message || "---"}
					</div>
				);
			},
			medium: true,
		},
		{
			label: "Approfondimento",
			key: "deepening",
			customRender: (item) => {
				const deepening = item?.deepening?.split(",");
				return (
					<div className="d-flex flex-column gap-1">
						{deepening
							?.filter((d) => d)
							?.map((d, k) => {
								const current = valuationAnalysisOption.filter(
									(o) => o.value === d,
								)?.[0];
								return (
									<div
										key={k}
										className={"severity" + (current ? " deepening" : "")}
									>
										{current?.label || "---"}
									</div>
								);
							})}
						{!deepening ? "---" : ""}
					</div>
				);
			},
			medium: true,
		},
	);

	const groupedReports = {};
	categories
		.sort((a, b) => a.name?.localeCompare(b.name))
		.forEach((c) => {
			const currentCategory = c.name;
			const members = reports?.filter(
				(r) => r?.playerCategory?.name === currentCategory,
			);
			groupedReports[currentCategory] = sortReports(members);
		});

	const listConfiguration = {
		data: grouped ? groupedReports : sortReports(reports),
		onClick: (report) => navigate("/scouting/reports/" + report.id),
	};

	return (
		<CardsContainer
			tableComponent={
				<CardsTable
					breadcrumbs={breadcrumbs}
					fullWidth
					fullHeight={fullHeight}
					middleHeight={!fullHeight}
					filtersData={filters || undefined}
					configurationColumns={columns}
					data={sortReports(reports)}
					handleClick={(report) => {
						navigate("/scouting/reports/" + report.id);
					}}
				/>
			}
			listComponent={
				<CardsList
					type="scouting_report"
					breadcrumbs={breadcrumbs}
					grouped={grouped}
					filtersData={filters || undefined}
					listConfiguration={listConfiguration}
					defaultGroupSortedOrder="descend"
				/>
			}
		/>
	);
}
